import React, { useState } from "react";
import * as Sentry from "@sentry/react-native";
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";

import EmailInput from "./Components/EmailInput";

import { ApiError } from "@/schemas/api/error";
import { isValidEmailAddress } from "@/schemas/forms/email";
import AuthService from "@/services/internal/auth";
import { alertUnexpectedApiError } from "@/utils/alerts";

const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();

  const validateEmail = (): boolean => {
    const emailValidation = isValidEmailAddress(email);
    if (!emailValidation.isValid) {
      Alert.alert("Invalid Email Address", emailValidation.message, [
        { text: "OK" },
      ]);
      return false;
    }
    return true;
  };

  const handleForgotPassword = async (): Promise<void> => {
    if (!validateEmail()) return;

    setIsLoading(true);
    const authService = new AuthService();

    try {
      await authService.passwordResetRequest(email);
      router.replace({
        params: { emailAddress: email },
        pathname: "/(authentication)/reset-password",
      });
    } catch (error) {
      Sentry.captureException(error);
      const apiError = error as ApiError;
      alertUnexpectedApiError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <StatusBar style="auto" />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.formContainer}
      >
        <View style={styles.card}>
          <Text style={styles.header}>Forgot Password</Text>
          <Text style={styles.label}>Email</Text>
          <EmailInput
            value={email}
            onChangeText={setEmail}
            placeholder="Enter your email"
          />
          <TouchableOpacity
            testID="reset-password-button"
            style={styles.resetButton}
            onPress={handleForgotPassword}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.resetButtonText}>Reset Password</Text>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F7FAFC",
  },
  header: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "center",
    color: "#4A5568",
  },
  label: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
    color: "#4A5568",
  },
  formContainer: {
    width: "100%",
    maxWidth: 400,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 10,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  resetButton: {
    backgroundColor: "#4299E1",
    borderRadius: 5,
    padding: 15,
    alignItems: "center",
    marginTop: 10,
  },
  resetButtonText: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
});

export default ForgotPasswordForm;
